import type { FieldSchema } from '@tsagko/label-contracts'
import { useEffect, useMemo, useRef, useState } from 'react'
import { flattenSchema, useCmdK } from './CmdKContext'

interface FlatItem {
  entity: string
  field: FieldSchema
}

function describeType(type: FieldSchema['type']): string {
  if (typeof type === 'string') return type
  return type.kind
}

export function CmdK() {
  const { isOpen, close, schema, recent, recordRecent, onPick } = useCmdK()
  const [query, setQuery] = useState('')
  const [highlight, setHighlight] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  const allItems = useMemo(() => flattenSchema(schema), [schema])

  const filtered = useMemo<FlatItem[]>(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) {
      const recentItems = recent
        .map(key => allItems.find(i => i.field.key === key))
        .filter((x): x is FlatItem => Boolean(x))
      const other = allItems.filter(i => !recent.includes(i.field.key))
      return [...recentItems, ...other]
    }
    return allItems.filter(({ entity, field }) => {
      const hay =
        `${entity} ${field.key} ${field.label} ${describeType(field.type)}`.toLowerCase()
      return hay.includes(needle)
    })
  }, [allItems, query, recent])

  useEffect(() => {
    if (!isOpen) {
      setQuery('')
      setHighlight(0)
      return
    }
    const id = setTimeout(() => inputRef.current?.focus(), 10)
    return () => clearTimeout(id)
  }, [isOpen])

  useEffect(() => {
    setHighlight(0)
  }, [query, isOpen])

  if (!isOpen) return null

  const choose = (item: FlatItem) => {
    recordRecent(item.field.key)
    onPick?.(item.field.key)
    close()
  }

  const handleKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      close()
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlight(h => Math.min(filtered.length - 1, h + 1))
      return
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlight(h => Math.max(0, h - 1))
      return
    }
    if (e.key === 'Enter') {
      e.preventDefault()
      const item = filtered[highlight]
      if (item) choose(item)
    }
  }

  return (
    <div
      data-testid="cmdk-root"
      role="dialog"
      aria-modal="true"
      aria-label="Schema field picker"
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-10"
      onKeyDown={handleKey}
      onClick={close}
    >
      <div
        className="w-full max-w-xl overflow-hidden rounded-lg bg-white shadow-2xl dark:bg-stone-900"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b border-stone-200 px-4 py-3 dark:border-stone-800">
          <span className="text-stone-400">⌘K</span>
          <input
            ref={inputRef}
            data-testid="cmdk-input"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search schema fields…"
            className="w-full bg-transparent text-sm outline-none"
          />
        </div>
        <div className="max-h-96 overflow-auto">
          {filtered.length === 0 && (
            <p className="p-4 text-sm text-stone-400">No matches.</p>
          )}
          <ul className="py-2">
            {filtered.map((item, index) => {
              const isHighlighted = index === highlight
              return (
                <li key={item.field.key}>
                  <button
                    type="button"
                    data-testid="cmdk-item"
                    data-field-key={item.field.key}
                    onClick={() => choose(item)}
                    onMouseEnter={() => setHighlight(index)}
                    className={[
                      'flex w-full items-center justify-between gap-3 px-4 py-2 text-left text-sm transition',
                      isHighlighted
                        ? 'bg-sky-100 text-sky-900 dark:bg-sky-950 dark:text-sky-100'
                        : 'hover:bg-stone-100 dark:hover:bg-stone-800',
                    ].join(' ')}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2">
                        <span className="text-[10px] uppercase tracking-wide text-stone-500">
                          {item.entity}
                        </span>
                        <span className="font-medium">{item.field.label}</span>
                      </div>
                      <div className="truncate font-mono text-[11px] text-stone-500">
                        {item.field.key}
                      </div>
                    </div>
                    <span className="rounded bg-stone-200 px-1.5 py-0.5 font-mono text-[10px] text-stone-700 dark:bg-stone-800 dark:text-stone-300">
                      {describeType(item.field.type)}
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
        <div className="flex items-center justify-between border-t border-stone-200 px-4 py-2 text-[10px] text-stone-400 dark:border-stone-800">
          <span>↑↓ navigate · Enter pick · Esc close</span>
          <span>{filtered.length} field{filtered.length === 1 ? '' : 's'}</span>
        </div>
      </div>
    </div>
  )
}
